import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { Performer } from "@shared/schema";

interface UsePerformerMutationsProps {
  jamNightId?: number;
}

export function usePerformerMutations({ jamNightId }: UsePerformerMutationsProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const updatePerformerMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<Performer>;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/performers/${id}`,
        updates,
      );
      return response.json();
    },
    onSuccess: () => {
      if (jamNightId) {
        queryClient.invalidateQueries({
          queryKey: [`/api/performers?jamNightId=${jamNightId}`],
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/performers"] });
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update performer",
        variant: "destructive",
      });
    },
  });

  const handleOffStage = (performer: Performer) => {
    // Calculate elapsed time from stageStartTime
    const elapsedMinutes = performer.stageStartTime 
      ? Math.floor((Date.now() - new Date(performer.stageStartTime).getTime()) / 1000 / 60)
      : 0;

    updatePerformerMutation.mutate({
      id: performer.id,
      updates: {
        status: 'participant',
        stageStartTime: null,
        timeOnStage: (performer.timeOnStage || 0) + elapsedMinutes,
        played: true,
      },
    });
  };

  return {
    updatePerformerMutation,
    handleOffStage,
  };
}